import React, {useCallback, useEffect, useRef, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {ChevronDownIcon, PlusIcon} from "./icons";

export interface DictionaryOption {
    id: string;
    name: string;
}

interface Props {
    // football dictionary endpoint: 'clubs' | 'seasons' | 'patches'
    endpoint: string;
    value: string;
    valueName?: string;
    onChange: (id: string, name: string) => void;
    placeholder?: string;
    className?: string;
}

/**
 * Autocomplete + create-on-the-fly select for the football dictionaries
 * (team / season / patch). Mirrors BrandSearchSelect but loads options
 * dynamically from the server as the user types.
 */
const FootballDictionarySelect: React.FC<Props> = ({
    endpoint,
    value,
    valueName,
    onChange,
    placeholder = "Найти или создать",
    className = ""
}) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<DictionaryOption[]>([]);
    const [creating, setCreating] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const displayValue = open ? query : (valueName ?? '');
    const exactMatch = query.trim() && options.some(o => o.name.toLowerCase() === query.trim().toLowerCase());
    const showCreate = query.trim() && !exactMatch && !creating;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        const handle = setTimeout(async () => {
            try {
                const api = await createAxiosInstance();
                const res = await api.get<DictionaryOption[]>(`/football/${endpoint}`, {params: {q: query.trim()}});
                setOptions(res.data ?? []);
            } catch (err) {
                console.error(`Failed to search ${endpoint}:`, err);
                setOptions([]);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [query, open, endpoint]);

    const handleCreate = useCallback(async () => {
        const name = query.trim();
        if (!name) return;
        setCreating(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<DictionaryOption>(`/football/${endpoint}`, {name});
            onChange(res.data.id, res.data.name);
            setQuery('');
            setOpen(false);
        } catch (err) {
            console.error(`Failed to create ${endpoint}:`, err);
        } finally {
            setCreating(false);
        }
    }, [query, endpoint, onChange]);

    const handleSelect = useCallback((o: DictionaryOption) => {
        onChange(o.id, o.name);
        setQuery('');
        setOpen(false);
    }, [onChange]);

    const handleClear = useCallback(() => {
        onChange('', '');
        setQuery('');
        setOpen(false);
    }, [onChange]);

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div className="flex gap-1">
                <input
                    type="text"
                    value={displayValue}
                    onChange={e => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    className="input-field flex-1"
                />
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="px-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700"
                >
                    <ChevronDownIcon className={`w-5 h-5 transition-transform ${open ? 'rotate-180' : ''}`}/>
                </button>
            </div>
            {open && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {options.length === 0 && !showCreate && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Ничего не найдено</div>
                    )}
                    {options.map(o => (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => handleSelect(o)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${value === o.id ? 'bg-primary-50 dark:bg-primary-900/30' : ''}`}
                        >
                            {o.name}
                        </button>
                    ))}
                    {showCreate && (
                        <button
                            type="button"
                            onClick={handleCreate}
                            disabled={creating}
                            className="w-full text-left px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 border-t border-gray-200 dark:border-gray-600"
                        >
                            <PlusIcon className="w-4 h-4"/>
                            {creating ? 'Создание...' : `Создать «${query.trim()}»`}
                        </button>
                    )}
                    {value && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-600"
                        >
                            Очистить
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default FootballDictionarySelect;
